// src/components/layouts/ClassicLayout.tsx
'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { getMenuOptions } from '@/components/menu';

export default function ClassicLayout({ children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL }: any) {
  const pathname = usePathname();
  // Estado para móviles (oculta/muestra todo el menú)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Estado para desktop (contrae/expande a Mini-Sidebar)
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  
  const isActive = (path: string) => pathname === path;
  const menuConfig = getMenuOptions(userRole);

  // 💡 Extraemos ambas URLs para usarlas dinámicamente según el estado del menú
  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL}${config.logo_url}`) : null;
  const faviconUrl = config?.favicon_url ? (config.favicon_url.startsWith('http') ? config.favicon_url : `${API_URL}${config.favicon_url}`) : null;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex overflow-hidden font-sans">
      
      {/* 📱 OVERLAY MÓVIL */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* 🖥️ SIDEBAR DINÁMICO (Responsive + Colapsable) */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out shadow-[1px_0_10px_rgba(0,0,0,0.02)]
          ${isMobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'} 
          ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
        `}
      >
        <div className={`flex items-center h-20 border-b border-gray-100 transition-all ${isCollapsed ? 'justify-center px-0' : 'justify-between px-6'}`}>
          
          {/* 🚀 Logo Dinámico (Intercambia entre Logo Extendido y Favicon) */}
          <div className={`flex items-center overflow-hidden h-10 w-full ${isCollapsed ? 'justify-center' : ''}`}>
            {isCollapsed ? (
              // VISTA CONTRAÍDA: Muestra el Favicon (Icono Cuadrado)
              faviconUrl ? (
                <img src={faviconUrl} alt="Icono" className="h-8 w-8 object-contain mx-auto animate-fade-in" title={config?.company_name} />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm mx-auto shrink-0 animate-fade-in">
                  {(config?.company_name || "C").charAt(0)}
                </div>
              )
            ) : (
              // VISTA EXPANDIDA: Muestra el Logo Completo
              logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-full w-auto object-contain animate-fade-in" title={config?.company_name} />
              ) : (
                <div className="flex items-center gap-3 animate-fade-in">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                    {(config?.company_name || "C").charAt(0)}
                  </div>
                  <span className="font-bold text-lg tracking-tight truncate max-w-[130px] whitespace-nowrap">
                    {config?.company_name || "Cash Money"}
                  </span>
                </div>
              )
            )}
          </div>

          {/* Rol Badge (Solo visible si está expandido) */}
          {!isCollapsed && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold bg-gray-100 text-gray-500 uppercase tracking-widest hidden sm:block shrink-0 animate-fade-in">
              {userRole}
            </span>
          )}
        </div>
        
        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin">
          {menuConfig.map((group, index) => (
            <div key={index}>
              {/* Título de Sección */}
              <h3 className={`text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 transition-all ${isCollapsed ? 'text-center opacity-50 px-0' : 'px-3 opacity-100'}`}>
                {isCollapsed ? '—' : group.section}
              </h3>
              
              <ul className="space-y-1.5">
                {group.items.map((item: any) => {
                  const active = isActive(item.path);
                  return (
                    <li key={item.path} title={isCollapsed ? item.name : ''}>
                      <Link 
                        href={item.path} 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center transition-all duration-200 group
                          ${isCollapsed ? 'justify-center p-3 rounded-xl' : 'gap-3 px-3 py-2.5 rounded-xl'}
                          ${active ? 'bg-blue-50 text-blue-700 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'}
                        `}
                      >
                        <span className={`text-xl transition-all duration-200 ${active ? 'scale-110' : 'grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100'}`}>
                          {item.icon}
                        </span>
                        
                        {!isCollapsed && (
                          <span className="text-sm truncate whitespace-nowrap animate-fade-in">
                            {item.name}
                          </span>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* 📦 CONTENIDO PRINCIPAL FLUIDO */}
      <main className={`flex-1 flex flex-col min-w-0 overflow-hidden relative transition-all duration-300 ease-in-out ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        
        {/* HEADER TOP */}
        <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8 shrink-0 relative z-20">
          <div className="flex items-center gap-2 lg:gap-4">
            
            {/* Botón Hamburguesa (Móvil) */}
            <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            
            {/* Botón Colapsar (Desktop) */}
            <button onClick={() => setIsCollapsed(!isCollapsed)} className="hidden lg:flex p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors" title="Alternar Menú">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>

            <h1 className="text-xl font-bold text-gray-800 tracking-tight truncate">{title}</h1>
            {userRole && <span className="hidden md:inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 uppercase tracking-widest border border-gray-200">{userRole}</span>}
          </div>
          
          <div className="flex items-center gap-5">
            <div className="relative">
              <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 focus:outline-none cursor-pointer p-1.5 rounded-2xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-bold text-gray-800">{userName}</p>
                  <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{userRole}</p>
                </div>
                {userAvatar ? (
                   <img src={userAvatar} alt={userName} className="w-10 h-10 rounded-full border border-gray-200 shadow-sm object-cover" />
                ) : (
                   <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-sm border border-blue-200 shadow-sm">
                     {userInitials}
                   </div>
                )}
              </button>
              
              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 z-50 animate-fade-in">
                  <div className="px-4 py-3 border-b border-gray-100 mb-2 sm:hidden">
                    <p className="text-sm font-bold text-gray-800">{userName}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">{userRole}</p>
                  </div>
                  <button onClick={handleLogout} className="w-full text-left flex items-center gap-3 px-5 py-2.5 text-sm text-red-600 hover:bg-red-50 font-bold transition-colors cursor-pointer">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        
        {/* ÁREA DE TRABAJO (Fluida sin límites estrictos) */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-4 lg:p-8 relative z-10 scrollbar-thin w-full">
          {children}
        </div>
      </main>
    </div>
  )
}